import { apiFetch } from "./api";

export interface AppSettingEntry {
  key: string;
  type: "string" | "int" | "secret";
  secret: boolean;
  set: boolean;
  value: string | null;
  updatedAt: number | null;
  updatedBy: number | null;
}

export interface SyncJobRow {
  name: string;
  cadenceSec: number;
  enabled: boolean;
  nextRunAt: number;
  lastRunAt: number | null;
  lastStatus: "ok" | "error" | null;
  lastError: string | null;
  lastSummary: string | null;
  cursor: string | null;
  inFlight: boolean;
}

function extractMessage(body: unknown): string | null {
  if (typeof body === "object" && body !== null && "message" in body) {
    const m = body.message;
    return typeof m === "string" ? m : null;
  }
  return null;
}

class AdminSystemApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    const message = extractMessage(body) ?? `admin-system api ${res.status}`;
    throw new AdminSystemApiError(message, res.status);
  }
  const data: unknown = await res.json();
  return data as T;
}

export async function listSettings(): Promise<AppSettingEntry[]> {
  return readJson<AppSettingEntry[]>(await apiFetch("/api/admin/settings"));
}

export async function patchSetting(key: string, value: string | null): Promise<AppSettingEntry> {
  const res = await apiFetch(`/api/admin/settings/${encodeURIComponent(key)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ value }),
  });
  return readJson<AppSettingEntry>(res);
}

export async function listSyncJobs(): Promise<SyncJobRow[]> {
  return readJson<SyncJobRow[]>(await apiFetch("/api/admin/sync-jobs"));
}

export async function runSyncJobNow(name: string): Promise<void> {
  const res = await apiFetch(`/api/admin/sync-jobs/${encodeURIComponent(name)}/run-now`, {
    method: "POST",
  });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    const message = extractMessage(body) ?? `admin-system api ${res.status}`;
    throw new AdminSystemApiError(message, res.status);
  }
}

export async function patchSyncJob(
  name: string,
  patch: { enabled?: boolean; cadenceSec?: number },
): Promise<SyncJobRow> {
  const res = await apiFetch(`/api/admin/sync-jobs/${encodeURIComponent(name)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  return readJson<SyncJobRow>(res);
}
